import Pusher from 'pusher-js';

// This is necessary for sending cookies
// See: https://github.com/pusher/pusher-js/issues/62
Pusher.Runtime.createXHR = () => {
  const xhr = new XMLHttpRequest();
  xhr.withCredentials = true;
  return xhr;
};

const config = (authEndpoint, trackingToken, pusherCluster) => {
  const opts = {
    authEndpoint,
    auth: {
      headers: {
        'X-Kustomer': 'kustomer',
        'x-kustomer-tracking-token': trackingToken,
      },
    },

    encrypted: true,
    disableStats: true,
  };

  if (pusherCluster) {
    opts.cluster = pusherCluster;
  }

  return opts;
};

class Socket {
  constructor() {
    this._pusher = null;
    this.resetPusherProperties();
  }

  channelAlreadyJoined(channelName) {
    return !!this._joinedChannels[channelName];
  }

  disconnect() {
    return new Promise(resolve => {
      if (this._pusher) {
        this._pusher.disconnect();
        this._pusher = null;
      }
      this.resetPusherProperties();
      return resolve();
    });
  }

  init({ pusherAuthEndpoint, trackingToken, pusherAccessKey, pusherCluster }) {
    if (this._pusher) {
      this.disconnect();
    }
    this._pusher = new Pusher(pusherAccessKey, config(pusherAuthEndpoint, trackingToken, pusherCluster));
  }

  joinAppChannel(channelName, onEvent) {
    return this.joinChannel('app', channelName, onEvent);
  }

  joinChatActivityChannel(channelName, onEvent) {
    return this.joinChannel('activity', channelName, onEvent);
  }

  joinCustomerPresenceChannel(channelName, onEvent) {
    return this.joinChannel('customerPresence', channelName, onEvent);
  }

  sendChatActivity(activityData, eventName) {
    const channel = this._channels.activity.channel;

    if (!channel) {
      return Promise.resolve();
    }

    return channel.trigger(eventName, activityData);
  }

  joinChannel(id, channelName, onEvent) {
    return new Promise(resolve => {
      if (!channelName || this.channelAlreadyJoined(channelName)) {
        return resolve();
      }

      const channel = this._channels[id];
      if (!channel.name || channel.name !== channelName) {
        this.leaveChannel(id);

        channel.channel = this._pusher.subscribe(channelName);
        channel.name = channelName;
        this.listen(channel.channel, onEvent);
        this._joinedChannels[channelName] = true;
      }

      return resolve();
    });
  }

  leaveChannel(id) {
    const channel = this._channels[id];
    if (channel && channel.name) {
      this._pusher.unsubscribe(channel.name);
      this._joinedChannels[channel.name] = false;
    }
  }

  resetPusherActivityChannel() {
    this._channels.activity = {
      name: null,
      channel: null,
    };
  }

  resetPusherPresenceChannel() {
    this._channels.customerPresence = {
      name: null,
      channel: null,
    };
  }

  leaveChatActivityChannel() {
    this.leaveChannel('activity');
  }

  leaveCustomerPresenceChannel() {
    this.leaveChannel('customerPresence');
  }

  listen(channel, onEvent) {
    if (!channel) return;
    channel.bind_global(onEvent);
  }

  resetPusherProperties() {
    this._joinedChannels = {};
    this._channels = {
      app: {
        name: null,
        channel: null,
      },
      activity: {
        name: null,
        channel: null,
      },
      customerPresence: {
        name: null,
        channel: null,
      },
    };
  }
}

// Singleton
export default new Socket();
